import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Box, chakra } from '@chakra-ui/react';
import { Bell } from 'lucide-react';

type BellButtonProps = {
  unreadCount: number;
  open?: boolean;
} & ComponentPropsWithoutRef<typeof chakra.button>;

/** Header bell. Forwards its ref so the desktop popover can use it as the trigger. */
export const BellButton = forwardRef<HTMLButtonElement, BellButtonProps>(function BellButton(
  { unreadCount, open = false, ...rest },
  ref,
) {
  return (
    <chakra.button
      ref={ref}
      type="button"
      aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      w="40px"
      h="40px"
      p="0"
      border="none"
      borderRadius="full"
      bg={open ? 'volyx.primaryLight' : 'white'}
      color={open ? 'volyx.action' : 'volyx.primary'}
      boxShadow="0 1px 3px rgba(31, 41, 50, 0.08)"
      cursor="pointer"
      transition="background-color 0.15s ease, box-shadow 0.15s ease"
      _hover={{ boxShadow: '0 2px 6px rgba(31, 41, 50, 0.14)' }}
      _focusVisible={{ outline: '2px solid', outlineColor: 'volyx.action', outlineOffset: '2px' }}
      {...rest}
    >
      <Bell size={20} aria-hidden />
      {unreadCount > 0 && (
        <Box
          as="span"
          aria-hidden
          position="absolute"
          top="-4px"
          right="-4px"
          minW="18px"
          h="18px"
          px="5px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="full"
          bg="volyx.error"
          color="white"
          fontSize="11px"
          lineHeight="14px"
          fontWeight="bold"
          boxShadow="0 0 0 2px white"
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </Box>
      )}
    </chakra.button>
  );
});
